/**
 * 全站路由配置
 *
 * 建议:
 * 1. 代码中路由统一使用name属性跳转(不使用path属性)
 */
import Vue from "vue";
import Router from "vue-router";
import http from "@utils/httpRequest";
import { isURL } from "@utils/method";
import { clearLoginInfo } from "@utils/index";

Vue.use(Router);
// 解决重复点击路由导航报错问题
const routerPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return routerPush.call(this, location).catch((err) => err);
};

// 开发环境不使用懒加载, 因为懒加载页面太多的话会造成webpack热更新太慢, 所以只有生产环境使用懒加载
const _import = function(file) {
  return () => import("@/views/" + file + ".vue");
};

// 全局路由(无需嵌套上左右整体布局)
const globalRoutes = [
  {
    path: "/404",
    component: _import("error/404"),
    name: "404",
    meta: {
      title: "404未找到",
    },
  },
  {
    path: "/login",
    component: _import("login/login"),
    name: "login",
    meta: {
      title: "登录",
    },
  },
];

// 主入口路由(需嵌套上左右整体布局)
const mainRoutes = {
  path: "/",
  component: _import("main/main"),
  name: "main",
  redirect: {
    name: "home",
  },
  meta: {
    title: "主入口整体布局",
  },
  children: [
    // 通过meta对象设置路由展示方式
    // 1. isTab: 是否通过tab展示内容, true: 是, false: 否
    // 2. iframeUrl: 是否通过iframe嵌套展示内容, '以http[s]://开头': 是, '': 否
    // 提示: 如需要通过iframe嵌套展示内容, 但不通过tab打开, 请自行创建组件使用iframe处理!
    {
      path: "/home",
      component: _import("home/home"),
      name: "home",
      meta: {
        title: "首页",
      },
    },
    {
      path: "/config-demand",
      component: _import("modules/config/demand"),
      name: "xuqiu",
      meta: {
        title: "需求管理",
      },
    },
    {
      path: "/config-suggestions",
      component: _import("modules/config/suggestions"),
      name: "zxjy",
      meta: {
        title: "咨询建议",
      },
    },
    {
      path: "/home/list",
      component: _import("modules/config/child-components/list"),
      name: "list",
      meta: {
        title: "好网货",
      },
    },
    {
      path: "/home/info",
      component: _import("modules/config/child-components/info"),
      name: "info",
      meta: {
        title: "好网货1",
      },
    },
    {
      path: "/home/status",
      component: _import("modules/config/child-components/status"),
      name: "status",
      meta: {
        title: "好网货2",
      },
    },
    {
      path: "/home/sign/:id",
      component: _import("child-pages/detailSignInfo"),
      name: "detailSign",
      meta: {
        title: "报名人员列表",
      },
    },
    {
      path: "/data/detail/:id",
      component: _import("child-pages/dataDetail"),
      name: "dataDetail",
      meta: {
        title: "数据快报详情",
      },
    },
    {
      path: "/home/activitiesReview/:id",
      component: _import("child-pages/activitiesReview"),
      name: "activitiesReview",
      meta: {
        title: "活动总览",
      },
    },
    {
      path: "/home/activitiesReviewa/:id",
      component: _import("child-pages/activitiesReviewa"),
      name: "activitiesReviewa",
      meta: {
        title: "活动总览",
      },
    },
    {
      path: "/home/uploadgrades/:id",
      component: _import("child-pages/updatatrader"),
      name: "uploadgrades",
      meta: {
        title: "成绩上传",
      },
    },
    {
      path: "/home/uploadgrades2/:id",
      component: _import("child-pages/updatatrader2"),
      name: "uploadgrades2",
      meta: {
        title: "成绩上传",
      },
    },

  ],
  beforeEnter(to, from, next) {
    let token = Vue.cookie.get("token");
    if (!token || !/\S/.test(token)) {
      clearLoginInfo();
      next({
        name: "login",
      });
    }
    next();
  },
};

const router = new Router({
  mode: "hash",
  scrollBehavior: () => ({
    y: 0,
  }),
  isAddDynamicMenuRoutes: false, // 是否已经添加动态(菜单)路由
  routes: globalRoutes.concat(mainRoutes),
});

router.beforeEach((to, from, next) => {
  // 添加动态(菜单)路由
  // 1. 已经添加 or 全局路由, 直接访问
  // 2. 获取菜单列表, 添加并保存本地存储
  if (
    router.options.isAddDynamicMenuRoutes ||
    fnCurrentRouteType(to, globalRoutes) === "global"
  ) {
    next();
  } else {
    http({
      url: http.adornUrl("/sys/menu/nav"),
      method: "get",
      params: http.adornParams(),
    })
      .then(({ data }) => {
        if (data && data.code === 0) {
          fnAddDynamicMenuRoutes(data.data.menuList);
          router.options.isAddDynamicMenuRoutes = true;
          sessionStorage.setItem(
            "menuList",
            JSON.stringify(data.data.menuList || "[]")
          );
          sessionStorage.setItem(
            "permissions",
            JSON.stringify(data.data.permissions || "[]")
          );
          next({
            ...to,
            replace: true,
          });
        } else {
          sessionStorage.setItem("menuList", "[]");
          sessionStorage.setItem("permissions", "[]");
          next();
        }
      })
      .catch((e) => {
        console.log(
          `%c${e} 请求菜单列表和权限失败，跳转至登录页！！`,
          "color:blue"
        );
        router.push({
          name: "login",
        });
      });
  }
});

/**
 * 判断当前路由类型, global: 全局路由, main: 主入口路由
 * @param {*} route 当前路由
 */
function fnCurrentRouteType(route, globalRoutes = []) {
  var temp = [];
  for (var i = 0; i < globalRoutes.length; i++) {
    if (route.path === globalRoutes[i].path) {
      return "global";
    } else if (
      globalRoutes[i].children &&
      globalRoutes[i].children.length >= 1
    ) {
      temp = temp.concat(globalRoutes[i].children);
    }
  }
  return temp.length >= 1 ? fnCurrentRouteType(route, temp) : "main";
}

/**
 * 添加动态(菜单)路由
 * @param {*} menuList 菜单列表
 * @param {*} routes 递归创建的动态(菜单)路由
 */
function fnAddDynamicMenuRoutes(menuList = [], routes = []) {
  var temp = [];
  for (var i = 0; i < menuList.length; i++) {
    if (menuList[i].list && menuList[i].list.length >= 1) {
      temp = temp.concat(menuList[i].list);
    } else if (menuList[i].url && /\S/.test(menuList[i].url)) {
      menuList[i].url = menuList[i].url.replace(/^\//, "");
      var route = {
        path: menuList[i].url.replace("/", "-"),
        component: null,
        name: menuList[i].url.replace("/", "-"),
        meta: {
          menuId: menuList[i].id,
          title: menuList[i].name,
          isDynamic: true,
          isTab: true,
          iframeUrl: "",
        },
      };
      // url以http[s]://开头, 通过iframe展示
      if (isURL(menuList[i].url)) {
        route["path"] = `i-${menuList[i].id}`;
        route["name"] = `i-${menuList[i].id}`;
        route["meta"]["iframeUrl"] = menuList[i].url;
      } else {
        try {
          route["component"] = _import(`modules/${menuList[i].url}`) || null;
        } catch (e) {
          console.error(e);
        }
      }
      routes.push(route);
    }
  }
  if (temp.length >= 1) {
    fnAddDynamicMenuRoutes(temp, routes);
  } else {
    mainRoutes.name = "main-dynamic";
    mainRoutes.children = routes;
    router.addRoutes([
      mainRoutes,
      {
        path: "*",
        redirect: {
          name: "404",
        },
      },
    ]);
    sessionStorage.setItem(
      "dynamicMenuRoutes",
      JSON.stringify(mainRoutes.children || "[]")
    );
    // console.log("\n");
    // console.log(
    // "%c!<-------------------- 动态(菜单)路由 s -------------------->",
    //   "color:blue";
    // );
    // console.log(mainRoutes.children);
    // console.log(
    // "%c!<-------------------- 动态(菜单)路由 e -------------------->",
    //   "color:blue";
    // );
  }
}

export default router;
